import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { useTheme } from "./contexts/ThemeContext.jsx";
import Header from "./components/Header.jsx";
import ConfigPanel from "./components/ConfigPanel.jsx";
import TimeEntryForm from "./components/TimeEntryForm.jsx";
import Ressourcenplanung from "./components/Ressourcenplanung.jsx";
import DeleteTimeEntryModal from "./components/DeleteTimeEntryModal.jsx";
import ProjectOverview from "./components/ProjectOverview.jsx";
import * as ApiService from "./services/apiService.js";
import { getAllProjects } from "./services/azureProjectsService.js";
import { getProjectPhases, getAllPhases } from "./services/azureProjectPhasesService.js";
import { getAllRates } from "./services/azureRatesService.js";
import { useAuth } from "./hooks/useAuth";
import { useApiData } from "./hooks/useApiData";
import { useTimeTracking } from "./hooks/useTimeTracking";
import { useRouteDataLoader } from "./hooks/useRouteDataLoader";
import TeamManagement from "./components/TeamManagement.jsx";
import TeamPersonAuswertung from "./components/TeamPersonAuswertung.jsx";
import {
  saveSecureConfig,
  loadSecureConfig,
  clearSecureConfig,
  isConfigExpired,
} from "./utils/secureStorage.js";
import CalendarWrapper from "./components/CalendarWrapper.jsx";
import * as DateUtils from "./utils/dateUtils.js";
import { getRecentProjects } from "./utils/recentProjects.js";
import GleitzeitDisplay from "./components/GleitzeitDisplay.jsx";
import { canAccess, canEdit, getVisibleNavigationItems, isFreelancer, isElGatoOnly } from "./utils/permissions";
import ProjectManagement from "./components/ProjectManagement.jsx";
import CrmSystem from "./components/CrmSystem.jsx";
import Finanzen from "./components/Finanzen";
import WebsiteSettings from "./components/WebsiteSettings.jsx";
import ProjectPage from "./components/ProjectPage.jsx";
import AppLayout from "./layouts/AppLayout.jsx";
import ProjectPageWrapper from "./pages/ProjectPageWrapper.jsx";
import MyPendenzen from "./components/MyPendenzen.jsx";
import PendenzenProjectWrapper from "./pages/PendenzenProjectWrapper.jsx";
import CostsPageWrapper from "./pages/CostsPageWrapper.jsx";
import CostEstimateEditorWrapper from "./pages/CostEstimateEditorWrapper.jsx";
import TerminePageWrapper from "./pages/TerminePageWrapper.jsx";
import TimelineEditorWrapper from "./pages/TimelineEditorWrapper.jsx";
import ResearchPageWrapper from "./pages/ResearchPageWrapper.jsx";
import ResearchProjectList from "./components/research/ResearchProjectList.jsx";
import Controlling from "./components/Controlling.jsx";
import References from "./components/References.jsx";
import Dashboard from "./components/Dashboard.jsx";
import NextcloudSettings from "./components/NextcloudSettings.jsx";

const App = () => {
  const { user, loading: authLoading, accessDenied, deniedUserEmail, logout } = useAuth();
  const { getComputedThemeValue } = useTheme();

  // View State
  const [currentView, setCurrentView] = useState("zeiterfassung");
  const [currentWeek, setCurrentWeek] = useState(new Date());

  // Form State
  const [selectedCells, setSelectedCells] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [timeEntryToDelete, setTimeEntryToDelete] = useState(null);
  const [selectedProjectForEntry, setSelectedProjectForEntry] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);

  // Employee State
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Zuletzt verwendete Projekte (Rechtsklick-Schnellmodus im Kalender)
  const [recentProjects, setRecentProjects] = useState([]);
  const refreshRecentProjects = (employee) => {
    const target = employee || selectedEmployee;
    setRecentProjects(target?.uuid ? getRecentProjects(target.uuid) : []);
  };
  useEffect(() => {
    refreshRecentProjects(selectedEmployee);
  }, [selectedEmployee]);

  // API State
  const [apiConfig, setApiConfig] = useState(loadSecureConfig);
  const [apiConnected, setApiConnected] = useState(false);
  const [connectionAttempted, setConnectionAttempted] = useState(false);
  const [lastConnectionError, setLastConnectionError] = useState(null);

  // Projects available for time tracking (based on rates + active phases)
  const [localProjects, setLocalProjects] = useState([]);

  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Custom Hooks - Daten und Business-Logik ausgelagert
  // Freelancers and noAbaNinja users don't need API data - they only access Azure-based features
  const isFreelancerUser = isFreelancer(user);
  const isElGatoOnlyUser = isElGatoOnly(user);
  // Externe El-Gato-Person nutzt nur Azure-basierte Features, keine AbaNinja-Daten
  const skipAbaNinja = isFreelancerUser || isElGatoOnlyUser || !!user?.noAbaNinja;
  const apiData = useApiData(apiConnected && !skipAbaNinja);

  // Fallback activity types for users without AbaNinja connection
  const fallbackActivityTypes = skipAbaNinja ? [
    { uuid: "fallback_1",  activityNumber: 1,   unit: "hour", translations: { de: { description: "Arbeit" } } },
    { uuid: "fallback_100", activityNumber: 100, unit: "hour", translations: { de: { description: "Offerte" } } },
    { uuid: "fallback_101", activityNumber: 101, unit: "hour", translations: { de: { description: "Admin" } } },
    { uuid: "fallback_102", activityNumber: 102, unit: "hour", translations: { de: { description: "Strategie" } } },
    { uuid: "fallback_104", activityNumber: 104, unit: "hour", translations: { de: { description: "Projektqualität" } } },
    { uuid: "fallback_3",  activityNumber: 3,   unit: "hour", translations: { de: { description: "Kurse" } } },
    { uuid: "fallback_4",  activityNumber: 4,   unit: "hour", translations: { de: { description: "Berufsschule" } } },
    { uuid: "fallback_5",  activityNumber: 5,   unit: "day",  translations: { de: { description: "Ferien" } } },
    { uuid: "fallback_6",  activityNumber: 6,   unit: "hour", translations: { de: { description: "Ferien in h" } } },
    { uuid: "fallback_7",  activityNumber: 7,   unit: "hour", translations: { de: { description: "Umzug" } } },
    { uuid: "fallback_8",  activityNumber: 8,   unit: "day",  translations: { de: { description: "Hochzeit" } } },
    { uuid: "fallback_9",  activityNumber: 9,   unit: "day",  translations: { de: { description: "Geburt" } } },
    { uuid: "fallback_10", activityNumber: 10,  unit: "day",  translations: { de: { description: "Todesfall" } } },
    { uuid: "fallback_11", activityNumber: 11,  unit: "hour", translations: { de: { description: "Öffentliches Amt" } } },
    { uuid: "fallback_12", activityNumber: 12,  unit: "day",  translations: { de: { description: "Krankheit" } } },
    { uuid: "fallback_13", activityNumber: 13,  unit: "day",  translations: { de: { description: "Unfall BU" } } },
    { uuid: "fallback_14", activityNumber: 14,  unit: "day",  translations: { de: { description: "Unfall NBU" } } },
    { uuid: "fallback_15", activityNumber: 15,  unit: "hour", translations: { de: { description: "Arztbesuch" } } },
    { uuid: "fallback_16", activityNumber: 16,  unit: "day",  translations: { de: { description: "Mutterschaft" } } },
    { uuid: "fallback_17", activityNumber: 17,  unit: "day",  translations: { de: { description: "Militär / Zivilschutz" } } },
  ] : [];

  const effectiveActivityTypes = skipAbaNinja ? fallbackActivityTypes : apiData.activityTypes;

  // Route-based lazy loading: only fetch detail data when the current page needs it
  const { needsTimeTracking, needsLocalProjects } = useRouteDataLoader(apiData.loadDetailData);

  const timeTracking = useTimeTracking(
    needsTimeTracking ? selectedEmployee : null,
    currentWeek,
    apiData.contracts,
    effectiveActivityTypes
  );

  // Config Management - Skip for freelancers and noAbaNinja users
  useEffect(() => {
    // Freelancers and noAbaNinja users don't need AbaNinja API configuration
    if (skipAbaNinja) return;

    try {
      if (apiConfig.accountUuid && apiConfig.bearerToken) {
        saveSecureConfig(apiConfig);
        ApiService.configure(apiConfig);
      } else {
        clearSecureConfig();
        setConnectionAttempted(false);
        setLastConnectionError(null);
        setApiConnected(false);
      }
    } catch (error) {
      setError(`Konfigurationsfehler: ${error.message}`);
      setApiConfig({ accountUuid: "", bearerToken: "" });
      setApiConnected(false);
      setConnectionAttempted(false);
      setLastConnectionError(error);
    }
  }, [apiConfig, skipAbaNinja]);

  // Session Check - Skip for freelancers and noAbaNinja users
  useEffect(() => {
    // Freelancers and noAbaNinja users don't use AbaNinja sessions
    if (skipAbaNinja) return;

    const checkSession = () => {
      if (isConfigExpired() && apiConnected) {
        setApiConnected(false);
        setApiConfig({ accountUuid: "", bearerToken: "" });
        setError("Sitzung abgelaufen. Bitte melden Sie sich erneut an.");
      }
    };

    checkSession();
    const interval = setInterval(checkSession, 60000);
    return () => clearInterval(interval);
  }, [apiConnected, skipAbaNinja]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Load projects for Zeiterfassung: projects where the user has rates + active phases
  useEffect(() => {
    if (!needsLocalProjects || !user) return;

    const loadProjects = async () => {
      try {
        const [projects, allPhases, allRates] = await Promise.all([
          getAllProjects(),
          getAllPhases(),
          getAllRates(),
        ]);

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const entries = [];
        const addedKeys = new Set();

        const isPhaseActive = (phase) => {
          if (!phase.startDate || !phase.endDate) return false;
          const start = new Date(phase.startDate);
          const end = new Date(phase.endDate);
          start.setHours(0, 0, 0, 0);
          end.setHours(23, 59, 59, 999);
          return today >= start && today <= end;
        };

        // Determine current user's employee IDs
        const tm = user.teamMember;
        const userIds = new Set();
        if (tm?.employeeUuid) userIds.add(tm.employeeUuid);
        if (tm?.teamId) userIds.add(tm.teamId);

        // Build a set of project codes where user has a rate (individual or global)
        const projectCodesWithRate = new Set();
        allRates.forEach(r => {
          if ((r.employeeId === "_global" || userIds.has(r.employeeId)) && r.rate > 0) {
            projectCodesWithRate.add(r.projectCode);
          }
        });

        // Group phases by project code
        const phasesByProject = {};
        allPhases.forEach(phase => {
          if (!phasesByProject[phase.projectNumber]) phasesByProject[phase.projectNumber] = [];
          phasesByProject[phase.projectNumber].push(phase);
        });

        // Add projects with rates and active phases
        projectCodesWithRate.forEach(code => {
          const project = projects.find(p => p.code === code);
          const projectTitle = project?.title || code;
          const phases = phasesByProject[code] || [];
          phases.filter(isPhaseActive).forEach(phase => {
            const key = `${code}_${phase.id}`;
            if (addedKeys.has(key)) return;
            addedKeys.add(key);
            entries.push({
              code,
              title: projectTitle,
              phaseName: phase.name,
              phaseId: phase.id,
              localOnly: true,
            });
          });
        });

        setLocalProjects(entries);
      } catch (err) {
        console.error("Fehler beim Laden der Projekte:", err);
      }
    };

    loadProjects();
  }, [user, needsLocalProjects]);

  // For noAbaNinja users (non-freelancer), create a synthetic employee from team member data
  useEffect(() => {
    if (user?.noAbaNinja && !isFreelancerUser && !selectedEmployee) {
      const tm = user.teamMember;
      if (tm) {
        setSelectedEmployee({
          uuid: tm.teamId,
          firstName: tm.name?.split(" ")[0] || "",
          lastName: tm.name?.split(" ").slice(1).join(" ") || "",
          user: { email: tm.email },
          synthetic: true,
        });
      }
    }
  }, [user, isFreelancerUser, selectedEmployee]);

  // Auto-select employee based on email - Skip for freelancers and noAbaNinja users
  useEffect(() => {
    // Freelancers and noAbaNinja users don't need employee selection
    if (skipAbaNinja) return;

    if (user && apiData.employees.length > 0 && !selectedEmployee) {
      const userEmail =
        user.userDetails || user.email || user.userPrincipalName || user.name;

      const matchedEmployee = apiData.employees.find((emp) => {
        const empEmail = emp.user?.email?.toLowerCase();
        const testEmail = userEmail?.toLowerCase();
        return empEmail === testEmail;
      });

      if (matchedEmployee) {
        setSelectedEmployee(matchedEmployee);
      } else {
        setSelectedEmployee(apiData.employees[0]);
      }
    }
  }, [user, apiData.employees, selectedEmployee, skipAbaNinja]);

  // Auto-test connection - Skip for freelancers and noAbaNinja users
  useEffect(() => {
    // Freelancers and noAbaNinja users don't need AbaNinja API connection
    if (skipAbaNinja) return;

    const autoTestConnection = async () => {
      if (
        apiConfig.accountUuid &&
        apiConfig.bearerToken &&
        !apiConnected &&
        !connectionAttempted &&
        !loading
      ) {
        try {
          setLoading(true);
          setError(null);
          setConnectionAttempted(true);

          ApiService.configure(apiConfig);
          await ApiService.getEmployees();

          setApiConnected(true);
          setLastConnectionError(null);
          setSuccess("Verbindung automatisch hergestellt!");

          setTimeout(() => setSuccess(null), 2000);
        } catch (error) {
          const is401 =
            error.message.includes("401") ||
            error.message.includes("unauthorized");
          const errorMessage = is401
            ? "Ungültiger Bearer Token. Bitte überprüfen Sie Ihre Zugangsdaten."
            : `Automatische Verbindung fehlgeschlagen: ${error.message}`;
          setError(errorMessage);
          setLastConnectionError(error);
          setApiConnected(false);
        } finally {
          setLoading(false);
        }
      }
    };

    autoTestConnection();
  }, [
    apiConfig.accountUuid,
    apiConfig.bearerToken,
    apiConnected,
    connectionAttempted,
    loading,
    skipAbaNinja,
  ]);

  useEffect(() => {
    if (user && !authLoading) {
      const redirectPath = sessionStorage.getItem("redirectAfterLogin");
      if (redirectPath && redirectPath !== window.location.pathname) {
        sessionStorage.removeItem("redirectAfterLogin");
        window.location.replace(redirectPath);
      }
    }
  }, [user, authLoading]);

  // Helper Functions
  const updateApiConfig = (newConfig) => {
    setApiConfig(newConfig);
  };

  const testConnection = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!apiConfig.accountUuid || !apiConfig.bearerToken) {
        throw new Error("Bitte geben Sie Account UUID und Bearer Token ein.");
      }

      ApiService.configure(apiConfig);
      await ApiService.getEmployees();

      setApiConnected(true);
      setConnectionAttempted(true);
      setLastConnectionError(null);
      setSuccess("Verbindung erfolgreich!");

      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Manual connection test failed:", error);

      const is401 =
        error.message.includes("401") || error.message.includes("unauthorized");
      const errorMessage = is401
        ? "Authentifizierung fehlgeschlagen. Bearer Token ist ungültig oder abgelaufen."
        : `Verbindung fehlgeschlagen: ${error.message}`;

      setError(errorMessage);
      setLastConnectionError(error);
      setApiConnected(false);
      setConnectionAttempted(true);
    } finally {
      setLoading(false);
    }
  };

  const getAllNavigationItems = () => [
    {
      id: "zeiterfassung",
      label: "Zeiterfassung",
      permission: "zeiterfassung",
      requiresEmployee: true,
      path: "/main-timetracking",
    },
    {
      id: "ressourcenplanung",
      label: "Ressourcenplanung",
      permission: "ressourcenplanung",
      requiresEmployee: false,
      path: "/main-ressources",
    },
    {
      id: "projekte",
      label: "Projekte",
      permission: "projekte",
      requiresEmployee: false,
      path: "/main-projects",
      subtabs: [
        { id: "projekte-overview", label: "Übersicht", path: "/main-projects" },
        { id: "projekte-controlling", label: "Controlling", path: "/main-projects/controlling" },
        { id: "projekte-stundenauszug", label: "Stundenauszug", path: "/main-projects/stundenauszug" },
      ],
    },
    {
      id: "finanzen",
      label: "Finanzen",
      permission: "finanzen",
      requiresEmployee: false,
      path: "/main-finance",
      subtabs: [
        { id: "finance-cashflow", label: "Cashflow", path: "/main-finance/cashflow" },
        { id: "finance-szenarien", label: "Szenarien", path: "/main-finance/szenarien" },
        { id: "finance-projects", label: "Projekte", path: "/main-finance/projects" },
        { id: "finance-overview", label: "Übersicht & Grafik", path: "/main-finance/overview" },
        { id: "finance-budget", label: "Budget", path: "/main-finance/analysis" },
        { id: "finance-edit", label: "Budget Verwaltung", path: "/main-finance/edit" },
      ],
    },
    {
      id: "crm",
      label: "CRM",
      permission: "crm",
      requiresEmployee: false,
      path: "/main-crm",
      subtabs: [
        { id: "crm-persons", label: "Personen", path: "/main-crm/persons" },
        { id: "crm-organizations", label: "Organisationen", path: "/main-crm/organizations" },
      ],
    },
    {
      id: "team",
      label: "Team",
      permission: "team",
      requiresEmployee: false,
      path: "/main-team",
    },
    {
      id: "website",
      label: "website",
      permission: "website",
      requiresEmployee: false,
      path: "/main-website",
    },
    {
      id: "references",
      label: "Referenzen",
      permission: "references",
      requiresEmployee: false,
      path: "/main-references",
    },
    {
      id: "kostenschaetzung",
      label: "Kosten",
      permission: "kostenschaetzung",
      requiresEmployee: false,
      path: "/kosten",
    },
    {
      id: "termine",
      label: "Termine",
      permission: "termine",
      requiresEmployee: false,
      path: "/termine",
    },
    {
      id: "research",
      label: "Research",
      permission: "research",
      requiresEmployee: false,
      path: "/main-research",
    },
    {
      id: "meinePendenzen",
      label: "Pendenzen",
      permission: "meinePendenzen",
      requiresEmployee: false,
      path: "/main-pendenzen",
      subtabs: [
        { id: "pendenzen-mine", label: "Meine Pendenzen", path: "/main-pendenzen" },
        { id: "pendenzen-all", label: "Projektübersicht", path: "/main-pendenzen/projekte" },
      ],
    },
    {
      id: "config",
      label: "Konfiguration",
      permission: "config",
      requiresEmployee: false,
      path: "/main-config",
    },
  ];

  // Event Handlers - vereinfacht durch Custom Hooks
  const handleSubmitTimeEntry = async (formData) => {
    try {
      if (editingEntry) {
        await timeTracking.updateTimeEntry(editingEntry, formData);
        setSuccess("Zeiteintrag erfolgreich aktualisiert!");
      } else {
        await timeTracking.createTimeEntry(formData);
        setSuccess("Zeiteintrag(e) erfolgreich erstellt!");
        refreshRecentProjects();
      }
      setShowForm(false);
      setSelectedCells([]);
      setSelectedProjectForEntry(null);
      setEditingEntry(null);
    } catch (err) {
      setError(err.message);
    }
  };

  // Rechtsklick-Schnellmodus: bucht die aktuelle Zellauswahl direkt auf ein
  // zuletzt verwendetes Projekt, ohne das Formular zu oeffnen. Aktivitaetstyp
  // "Arbeit", Buchungstext bleibt bewusst leer (Nutzerwunsch).
  const handleQuickCreateEntry = async (project) => {
    if (!project?.code || selectedCells.length === 0) return;
    try {
      const timeBlocks = DateUtils.getContiguousTimeBlocks(selectedCells);
      const arbeitType = effectiveActivityTypes.find(
        (at) => at.translations?.de?.description === "Arbeit"
      );
      await timeTracking.createTimeEntry({
        activityTypeUuid: arbeitType?.uuid || "",
        description: "",
        contractUuid: "",
        localOnly: true,
        localProjectCode: project.code,
        localProjectName: project.name || project.code,
        isFullDay: false,
        timeBlocks,
      });
      setSuccess(`Zeiteintrag für ${project.code} erfasst!`);
      refreshRecentProjects();
      setSelectedCells([]);
    } catch (err) {
      setError("Fehler beim Schnellbuchen: " + err.message);
    }
  };

  const handleEmployeeChange = (employee) => {
    setSelectedEmployee(employee);
    setSelectedCells([]);
  };

  const handleDeleteTimeEntry = (timeEntry) => {
    setTimeEntryToDelete(timeEntry);
    setShowDeleteModal(true);
  };

  const handleEditTimeEntry = (timeEntry) => {
    setEditingEntry(timeEntry);
    setSelectedCells([]);
    setSelectedProjectForEntry(null);
    setShowForm(true);
  };

  // Verschieben/Groesse aendern eines Zeitblocks per Drag im Kalender.
  const handleAdjustTimeEntry = async (timeEntry, { date, startTime, endTime }) => {
    try {
      await timeTracking.adjustTimeEntry(timeEntry, { date, startTime, endTime });
    } catch (err) {
      setError("Fehler beim Verschieben des Zeiteintrags: " + err.message);
    }
  };

  const handleDeleteFromForm = (timeEntry) => {
    // Aus dem Edit-Formular heraus loeschen: Formular schliessen,
    // Loesch-Bestaetigung anzeigen.
    setShowForm(false);
    setEditingEntry(null);
    setTimeEntryToDelete(timeEntry);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (timeEntry) => {
    try {
      await timeTracking.deleteTimeEntry(timeEntry);
      setSuccess("Zeiteintrag erfolgreich gelöscht!");
      setShowDeleteModal(false);
      setTimeEntryToDelete(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setTimeEntryToDelete(null);
  };

  const handleCreateEntryWithProject = (projectNumber = null) => {
    setSelectedProjectForEntry(projectNumber);
    setShowForm(true);
  };

  const handleCellClick = (date, employee) => {
    setSelectedCells([{ date, employee }]);
    setShowForm(true);
  };

  const handleCellSelect = (cells) => {
    setSelectedCells(cells);
    if (cells.length > 0) {
      setShowForm(true);
    }
  };

  // Navigation
  const allNavigationItems = getAllNavigationItems();
  const navigationItems = user
    ? getVisibleNavigationItems(user, allNavigationItems)
    : [];

  const getPageTitle = () => {
    switch (currentView) {
      case "zeiterfassung":
        return "ZEITERFASSUNG";
      case "projectOverview":
        return "PROJEKTE";
      case "ressourcenplanung":
        return "RESSOURCENPLANUNG";
      case "team":
        return "TEAM";
      case "finanzen":
        return "FINANZEN";
      case "config":
        return "KONFIGURATION";
      case "projekte":
        return "PROJEKTERFASSUNG";
      default:
        return "ZEITERFASSUNG";
    }
  };

  // Loading & Authentication
  if (authLoading) {
    return <div className="loading">Anmeldung wird geprüft...</div>;
  }

  // Access Denied - User is authenticated but not registered in Team table
  if (accessDenied) {
    return (
      <div className="access-denied-page">
        <div className="access-denied-container">
          <div className="access-denied-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h2>Zugriff verweigert</h2>
          <p className="access-denied-message">
            Sie sind nicht als Teammitglied registriert.
          </p>
          <p className="access-denied-email">
            Angemeldete E-Mail: <strong>{deniedUserEmail || "Unbekannt"}</strong>
          </p>
          <p className="access-denied-help">
            Bitte kontaktieren Sie einen Administrator, um Ihren Zugang im Team-Bereich freizuschalten.
          </p>
          <div className="access-denied-actions">
            <button className="btn btn-secondary" onClick={logout}>
              Mit anderem Konto anmelden
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    // Speichere aktuelle URL für Redirect nach Login
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath !== "/") {
      sessionStorage.setItem("redirectAfterLogin", currentPath);
    }

    return (
      <div className="login-required">
        <h2>Anmeldung erforderlich</h2>
        <a href="/.auth/login/aad">Mit Azure AD anmelden</a>
      </div>
    );
  }

  // Render content based on current view (for legacy routes)
  const renderContent = (viewId, financeTab = null, crmTab = null, projectSubTab = null, pendenzenScope = "mine") => {
    // Freelancers, noAbaNinja users, and dashboard bypass API configuration
    if (!apiConnected && !skipAbaNinja && viewId !== "dashboard" && viewId !== "kostenschaetzung" && viewId !== "termine") {
      return (
        <div className="view-container">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">API Konfiguration</h3>
            </div>
            <div style={{ padding: "1.5rem" }}>
              <ConfigPanel
                config={apiConfig}
                onChange={updateApiConfig}
                onTest={testConnection}
                testResult={apiConnected}
                loading={loading}
              />
            </div>
          </div>
        </div>
      );
    }

    if (viewId !== "zeiterfassung" && viewId !== "dashboard" && !canAccess(user, viewId)) {
      return (
        <div className="access-denied">
          <h3>Zugriff verweigert</h3>
          <p>Sie haben keine Berechtigung für diesen Bereich.</p>
        </div>
      );
    }

    switch (viewId) {
      case "zeiterfassung":
        return selectedEmployee ? (
          <>
            <CalendarWrapper
              currentWeek={currentWeek}
              setCurrentWeek={setCurrentWeek}
              selectedCells={selectedCells}
              setSelectedCells={setSelectedCells}
              onCreateEntry={() => handleCreateEntryWithProject()}
              onDeleteEntry={handleDeleteTimeEntry}
              onEditEntry={handleEditTimeEntry}
              onAdjustEntry={handleAdjustTimeEntry}
              recentProjects={recentProjects}
              onQuickCreateEntry={handleQuickCreateEntry}
              timeEntries={timeTracking.timeEntries}
              contracts={apiData.contracts}
              loading={timeTracking.loading || apiData.loading}
              themeValues={{
                backgroundColor: getComputedThemeValue("--background-color"),
                textColor: getComputedThemeValue("--text-color"),
                textBgColor: getComputedThemeValue("--text-bg-color"),
                projectColor: getComputedThemeValue("--main-color"),
                projectBgColor: getComputedThemeValue("--second-color"),
              }}
            />
            <GleitzeitDisplay selectedEmployee={selectedEmployee} />
            {showForm && (
              <TimeEntryForm
                selectedCells={selectedCells}
                contracts={apiData.contracts}
                activityTypes={effectiveActivityTypes}
                products={apiData.products}
                onSubmit={handleSubmitTimeEntry}
                onCancel={() => {
                  setShowForm(false);
                  setSelectedProjectForEntry(null);
                  setEditingEntry(null);
                }}
                onDelete={handleDeleteFromForm}
                editingEntry={editingEntry}
                loading={timeTracking.loading}
                error={timeTracking.error}
                selectedProject={selectedProjectForEntry}
                selectedEmployee={selectedEmployee}
                localProjects={localProjects}
              />
            )}
            {showDeleteModal && (
              <DeleteTimeEntryModal
                timeEntry={timeEntryToDelete}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                loading={timeTracking.loading}
              />
            )}
          </>
        ) : (
          <div className="no-employee-selected">
            <p>Bitte wählen Sie einen Mitarbeiter aus, um die Zeiterfassung zu verwenden.</p>
          </div>
        );

      case "ressourcenplanung":
        return (
          <Ressourcenplanung
            readOnly={!canEdit(user, 'ressourcenplanung')}
          />
        );

      case "projekte":
        return (
          <ProjectManagement
            selectedEmployee={selectedEmployee}
            employees={apiData.employees}
            user={user}
            readOnlyControlling={!canEdit(user, 'controlling')}
            initialSubTab={projectSubTab}
          />
        );

      case "team":
        return <TeamManagement user={user} />;

      case "crm":
        return (
          <div className="crm-system-container">
            <CrmSystem initialTab={crmTab} />
          </div>
        );

      case "finanzen":
        return <Finanzen initialTab={financeTab} />;

      case "website":
        return <WebsiteSettings user={user} />;

      case "references":
        return <References />;

      case "research":
        // Übersicht der Research-Projekte; öffnet automatisch das
        // letztgeöffnete Projekt (/main-research/:researchProjectId).
        return <ResearchProjectList />;

      case "config":
        return (
          <>
            <ConfigPanel
              config={apiConfig}
              onChange={updateApiConfig}
              onTest={testConnection}
              testResult={apiConnected}
              loading={loading}
            />
            <NextcloudSettings />
          </>
        );

      case "kostenschaetzung":
        return <CostsPageWrapper />;

      case "termine":
        return <TerminePageWrapper />;

      case "meinePendenzen":
        return pendenzenScope === "project" ? (
          <PendenzenProjectWrapper />
        ) : (
          <MyPendenzen user={user} scope={pendenzenScope} />
        );

      case "dashboard":
        return (
          <Dashboard
            user={user}
            selectedEmployee={selectedEmployee}
            employees={apiData.employees}
            contracts={apiData.contracts}
            allContracts={apiData.allContracts}
            timeEntries={timeTracking.timeEntries}
            products={apiData.products}
            activityTypes={effectiveActivityTypes}
            apiConnected={apiConnected}
            apiConfig={apiConfig}
          />
        );

      default:
        return null;
    }
  };

  // Layout wrapper component for routes
  const LayoutWrapper = ({ viewId, financeTab = null, crmTab = null, projectSubTab = null, pendenzenScope = "mine", pageTitle }) => (
    <AppLayout
      employees={skipAbaNinja ? [] : apiData.employees}
      selectedEmployee={selectedEmployee}
      onEmployeeChange={handleEmployeeChange}
      apiConnected={apiConnected || skipAbaNinja}
      currentView={viewId}
      onViewChange={setCurrentView}
      navigationItems={navigationItems}
      user={user}
      pageTitle={pageTitle || getPageTitle()}
      isFreelancer={isFreelancerUser}
    >
      {renderContent(viewId, financeTab, crmTab, projectSubTab, pendenzenScope)}
    </AppLayout>
  );

  // Determine default route based on user role
  const defaultRoute = isFreelancer(user)
    ? "/main-projects"
    : isElGatoOnlyUser
      ? "/main-website"
      : "/main-timetracking";

  // Main Render
  return (
    <Routes>
      {/* Redirect root to appropriate default based on role */}
      <Route path="/" element={<Navigate to={defaultRoute} replace />} />

      {/* Main Navigation Routes */}
      <Route
        path="/main-timetracking"
        element={<LayoutWrapper viewId="zeiterfassung" pageTitle="ZEITERFASSUNG" />}
      />
      <Route
        path="/main-ressources"
        element={<LayoutWrapper viewId="ressourcenplanung" pageTitle="RESSOURCENPLANUNG" />}
      />
      <Route
        path="/main-projects"
        element={<LayoutWrapper viewId="projekte" projectSubTab="overview" pageTitle="PROJEKTE" />}
      />
      <Route
        path="/main-projects/controlling"
        element={<LayoutWrapper viewId="projekte" projectSubTab="controlling" pageTitle="PROJEKTE" />}
      />
      <Route
        path="/main-projects/controlling/:projectCode"
        element={<LayoutWrapper viewId="projekte" projectSubTab="controlling" pageTitle="PROJEKTE" />}
      />
      <Route
        path="/main-projects/stundenauszug"
        element={<LayoutWrapper viewId="projekte" projectSubTab="stundenauszug" pageTitle="PROJEKTE" />}
      />
      <Route
        path="/main-projects/stundenauszug/:projectCode"
        element={<LayoutWrapper viewId="projekte" projectSubTab="stundenauszug" pageTitle="PROJEKTE" />}
      />
      <Route
        path="/main-crm"
        element={<Navigate to="/main-crm/persons" replace />}
      />
      <Route
        path="/main-crm/persons"
        element={<LayoutWrapper viewId="crm" crmTab="persons" pageTitle="CRM" />}
      />
      <Route
        path="/main-crm/organizations"
        element={<LayoutWrapper viewId="crm" crmTab="organizations" pageTitle="CRM" />}
      />
      <Route
        path="/main-team"
        element={<LayoutWrapper viewId="team" pageTitle="TEAM" />}
      />
      <Route
        path="/team/:teamId/auswertung"
        element={
          <AppLayout
            employees={skipAbaNinja ? [] : apiData.employees}
            selectedEmployee={selectedEmployee}
            onEmployeeChange={handleEmployeeChange}
            apiConnected={apiConnected || skipAbaNinja}
            currentView="team"
            onViewChange={setCurrentView}
            navigationItems={navigationItems}
            user={user}
            pageTitle="AUSWERTUNG"
            isFreelancer={isFreelancerUser}
          >
            <TeamPersonAuswertung user={user} />
          </AppLayout>
        }
      />
      <Route
        path="/main-website"
        element={<LayoutWrapper viewId="website" pageTitle="WEBSITE" />}
      />
      <Route
        path="/main-references"
        element={<LayoutWrapper viewId="references" pageTitle="REFERENZEN" />}
      />
      <Route
        path="/main-research"
        element={<LayoutWrapper viewId="research" pageTitle="RESEARCH" />}
      />
      <Route
        path="/main-research/:researchProjectId"
        element={
          <AppLayout
            employees={skipAbaNinja ? [] : apiData.employees}
            selectedEmployee={selectedEmployee}
            onEmployeeChange={handleEmployeeChange}
            apiConnected={apiConnected || skipAbaNinja}
            currentView="research"
            onViewChange={setCurrentView}
            navigationItems={navigationItems}
            user={user}
            pageTitle="RESEARCH"
            isFreelancer={isFreelancerUser}
          >
            <ResearchPageWrapper />
          </AppLayout>
        }
      />
      <Route
        path="/main-config"
        element={<LayoutWrapper viewId="config" pageTitle="KONFIGURATION" />}
      />
      <Route
        path="/main-dashboard"
        element={<LayoutWrapper viewId="dashboard" pageTitle="DASHBOARD" />}
      />

      {/* Cost Estimates Routes */}
      <Route
        path="/kosten"
        element={<LayoutWrapper viewId="kostenschaetzung" pageTitle="KOSTEN" />}
      />
      <Route
        path="/kosten/:estimateId"
        element={
          <AppLayout
            employees={skipAbaNinja ? [] : apiData.employees}
            selectedEmployee={selectedEmployee}
            onEmployeeChange={handleEmployeeChange}
            apiConnected={apiConnected || skipAbaNinja}
            currentView="kostenschaetzung"
            onViewChange={setCurrentView}
            navigationItems={navigationItems}
            user={user}
            pageTitle="KOSTEN"
            isFreelancer={isFreelancerUser}
          >
            <CostEstimateEditorWrapper />
          </AppLayout>
        }
      />

      {/* Pendenzen (projektübergreifend) */}
      <Route
        path="/main-pendenzen"
        element={<LayoutWrapper viewId="meinePendenzen" pendenzenScope="mine" pageTitle="PENDENZEN" />}
      />
      <Route
        path="/main-pendenzen/projekte"
        element={<LayoutWrapper viewId="meinePendenzen" pendenzenScope="all" pageTitle="PENDENZEN" />}
      />
      <Route
        path="/main-pendenzen/projekte/:projectCode"
        element={<LayoutWrapper viewId="meinePendenzen" pendenzenScope="project" pageTitle="PENDENZEN" />}
      />

      {/* Termine (Gantt) Routes */}
      <Route
        path="/termine"
        element={<LayoutWrapper viewId="termine" pageTitle="TERMINE" />}
      />
      <Route
        path="/termine/:timelineId"
        element={
          <AppLayout
            employees={skipAbaNinja ? [] : apiData.employees}
            selectedEmployee={selectedEmployee}
            onEmployeeChange={handleEmployeeChange}
            apiConnected={apiConnected || skipAbaNinja}
            currentView="termine"
            onViewChange={setCurrentView}
            navigationItems={navigationItems}
            user={user}
            pageTitle="TERMINE"
            isFreelancer={isFreelancerUser}
          >
            <TimelineEditorWrapper />
          </AppLayout>
        }
      />

      {/* Finance Routes */}
      <Route
        path="/main-finance"
        element={<Navigate to="/main-finance/cashflow" replace />}
      />
      <Route
        path="/main-finance/cashflow"
        element={<LayoutWrapper viewId="finanzen" financeTab="cashflow" pageTitle="FINANZEN" />}
      />
      <Route
        path="/main-finance/szenarien"
        element={<LayoutWrapper viewId="finanzen" financeTab="szenarien" pageTitle="FINANZEN" />}
      />
      <Route
        path="/main-finance/projects"
        element={<LayoutWrapper viewId="finanzen" financeTab="projects" pageTitle="FINANZEN" />}
      />
      <Route
        path="/main-finance/overview"
        element={<LayoutWrapper viewId="finanzen" financeTab="overview" pageTitle="FINANZEN" />}
      />
      <Route
        path="/main-finance/analysis"
        element={<LayoutWrapper viewId="finanzen" financeTab="auswertung" pageTitle="FINANZEN" />}
      />
      <Route
        path="/main-finance/edit"
        element={<LayoutWrapper viewId="finanzen" financeTab="budget" pageTitle="FINANZEN" />}
      />

      {/* Project Sub-Routes */}
      <Route
        path="/projects/:projectSlug/overview"
        element={<ProjectPageWrapper defaultTab="info" />}
      />
      <Route
        path="/projects/:projectSlug/network"
        element={<ProjectPageWrapper defaultTab="netzwerk" />}
      />
      <Route
        path="/projects/:projectSlug/controlling"
        element={<ProjectPageWrapper defaultTab="controlling" />}
      />
      <Route
        path="/projects/:projectSlug/rates"
        element={<ProjectPageWrapper defaultTab="ansaetze" />}
      />
      <Route
        path="/projects/:projectSlug/plaene"
        element={<ProjectPageWrapper defaultTab="plaene" />}
      />
      <Route
        path="/projects/:projectSlug/sitzungen"
        element={<ProjectPageWrapper defaultTab="sitzungen" />}
      />
      <Route
        path="/projects/:projectSlug/website"
        element={<ProjectPageWrapper defaultTab="website" />}
      />
      <Route
        path="/projects/:projectSlug/gatoodle"
        element={<ProjectPageWrapper defaultTab="gatoodle" />}
      />

      {/* Legacy Project Routes - redirect to new structure */}
      <Route
        path="/projects/:projectSlug"
        element={<ProjectPageWrapper />}
      />
      <Route
        path="/main-projects/:projectCode/edit"
        element={<ProjectPageWrapper defaultTab="info" />}
      />
      <Route
        path="/main-projects/:projectCode/controlling"
        element={<Controlling isInline={false} readOnly={!canEdit(user, 'controlling')} />}
      />
      <Route
        path="/main-projects/:projectCode/network"
        element={<ProjectPageWrapper defaultTab="netzwerk" />}
      />

      {/* Legacy Controlling Route */}
      <Route
        path="/controlling/:projectCode"
        element={<Controlling readOnly={!canEdit(user, 'controlling')} />}
      />
    </Routes>
  );
};

export default App;
